<script setup lang="ts">
import { computed, inject, ref } from "vue";
import { storeToRefs } from "pinia";
import { type RouteParamValue, useRoute } from "vue-router";

import { useAuthStore } from "@/stores/Auth";
import { usePagingFilter } from "@/composables/filters/paging-filter";

import type { SearchContextWrapper } from "@tmi/types/context/search-context";
import { searchContextSymbol } from "@/constants";

import DynamicAd from "@/components/ads/DynamicAd.vue";
import ContentPreview from "@/components/content/ContentPreview.vue";
import CategoriesFilter from "@/components/filters/Categories.vue";
import DateRangeFilter from "@/components/filters/DateRange.vue";
import FreeNewsletterSignup from "@/components/signups/FreeNewsletterSignup.vue";
import PageLayout from '@/components/layout/PageLayout.vue';
import ProductsFilter from "@/components/filters/Products.vue";
import RecentlyViewedContent from "@/components/content/RecentlyViewedContent.vue";
import TextSearchFilter from "@/components/filters/TextSearch.vue";

const { isAuthenticated } = storeToRefs( useAuthStore() );

// Provide the search manager to all subcomponents
const injectedContext = inject< SearchContextWrapper | undefined >( searchContextSymbol );
if ( !injectedContext ) {
  throw new Error( `The expected provided value for 'searchContextSymbol' does not exist in this context.` );
}

const {
  contentCategories,
  contentTitle,
  contentType,
  highlightedContent,
  filteredContent,
  filteredContentPage,
  hasAnyResults,
  hideCategoryFilter,
  hideProductFilter,
  isLoading,
  searchPlaceholder,
  searchResultsLabel,
  showTaggedIn,
  hideDateFilter
} = injectedContext!;

const { pageNumber, pageSize } = usePagingFilter();

const route = useRoute();
const slug = computed< string | RouteParamValue[] | undefined >( () => route?.params?.slug );
const hasSlug = computed< boolean >( () => !!( slug.value ) );

const showCategoriesFilter = computed< boolean >( () => isAuthenticated.value && !!contentCategories && contentCategories.value.length > 1 && !hideCategoryFilter );
const showPagination = computed< boolean >( () => filteredContent.value.length > pageSize.value );
const totalRecords = computed< number >( () => filteredContent.value.length );
const showProductFilter = ref< boolean >( isAuthenticated.value && !!( contentTitle ) && !hideProductFilter );
const showTaggedInLabel = ref< boolean >( !!( showTaggedIn ) && hasSlug.value );
const showDateFilter = ref< boolean >( !hideDateFilter );
</script>

<template>
  <PageLayout :title="contentTitle">
    <ProgressSpinner v-if="isLoading" />
    <div v-else class="grid grid-cols-1 lg:grid-cols-3 gap-y-4 lg:gap-8">
      <main class="col-span-2 order-last lg:order-first">

        <div v-if="hasAnyResults" class="text-zinc-400 text-sm">{{ searchResultsLabel }}</div>
        <div v-else class="text-center text-zinc-400 h-full flex items-center justify-center">No results found</div>

        <div class="divide-y flex flex-col">
          <ContentPreview :slug="content.slug" :content-type="contentType!" v-for="( content ) in highlightedContent" :key="content.slug" :highlight="true" />
          <ContentPreview :slug="content.slug" :content-type="contentType!" v-for="( content ) in filteredContentPage" :key="content.slug" />
        </div>
        <Paginator v-model:first="pageNumber" v-if="showPagination" :totalRecords="totalRecords" :rows="pageSize" :alwaysShow="false" />
        <DynamicAd v-if="hasAnyResults" ad-type="banner" site-location="article" />
      </main>

      <aside class="space-y-5 w-full">
        <TextSearchFilter :placeholder="searchPlaceholder" />
        <DateRangeFilter v-if="showDateFilter"/>
        <ProductsFilter v-if="showProductFilter" />
        <CategoriesFilter v-if="showCategoriesFilter" :content-categories="contentCategories" />
        <FreeNewsletterSignup v-if="!isAuthenticated" />
        <RecentlyViewedContent v-if="isAuthenticated" />
        <DynamicAd ad-type="side-right" site-location="article" />
        <DynamicAd ad-type="popup" site-location="article" />

        <div v-if="showTaggedInLabel" class="text-base">
          Tagged in:<br>
          {{ `"${ slug }"` }}
        </div>
      </aside>

    </div>
  </PageLayout>
</template>
